import React, { useEffect, useMemo, useState } from 'react';
import { listTrackedLinks, createTrackedLink, deleteTrackedLink, shortLinkUrl } from '../lib/api';
import { Link2, Copy, Trash2, Megaphone, BarChart3, Plus, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function Marketing() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ destination_url: '', source: '', campaign: '', medium: 'share', note: '' });

  const load = async () => {
    setLoading(true);
    try { setLinks(await listTrackedLinks()); }
    catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    const total = links.reduce((a, l) => a + (l.clicks || 0), 0);
    const top = [...links].sort((a, b) => (b.clicks || 0) - (a.clicks || 0))[0];
    return { count: links.length, clicks: total, topSource: top?.source || '—' };
  }, [links]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.destination_url.startsWith('http')) return toast.error('Enter a full https:// URL');
    try {
      const link = await createTrackedLink(form);
      setLinks(l => [link, ...l]);
      setForm({ destination_url: '', source: '', campaign: '', medium: 'share', note: '' });
      toast.success('Link created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const doCopy = async (slug) => {
    try { await navigator.clipboard.writeText(shortLinkUrl(slug)); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this link? Analytics will be removed.')) return;
    try { await deleteTrackedLink(id); setLinks(l => l.filter(x => x.id !== id)); toast.success('Deleted'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="marketing-page">
      <div className="flex items-center gap-2 text-[#ff6b8a]"><Megaphone className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Marketing Suite</span></div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-2 leading-none">Tracked links & campaigns</h1>
      <p className="text-white/60 text-[14px] mt-1">Generate branded short links, tag them with source/campaign, and track every click.</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <Stat ic={Link2}     color="#22d3ee" label="Active links" value={summary.count} />
        <Stat ic={BarChart3} color="#ff6b8a" label="Total clicks" value={summary.clicks} />
        <Stat ic={Megaphone} color="#ffcf52" label="Top source"   value={summary.topSource} />
      </div>

      <div className="card-shell p-5 mt-8">
        <h3 className="row-title text-white">Create tracked link</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 mt-4">
          <input value={form.destination_url} onChange={e => setForm({ ...form, destination_url: e.target.value })} placeholder="https://your-video-url" className="input-field sm:col-span-2" data-testid="mkt-dest-url" />
          <input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="Source (twitter, bio, email…)" className="input-field" data-testid="mkt-source" />
          <input value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} placeholder="Campaign (optional)" className="input-field" data-testid="mkt-campaign" />
          <select value={form.medium} onChange={e => setForm({ ...form, medium: e.target.value })} className="input-field">
            <option value="share">share</option><option value="embed">embed</option><option value="bio">bio</option><option value="ad">ad</option><option value="email">email</option>
          </select>
          <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" className="input-field" />
          <button className="btn-primary sm:col-span-2" data-testid="mkt-create-btn"><Plus className="w-4 h-4" /> Create tracked link</button>
        </form>
      </div>

      <div className="card-shell mt-8 overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <h3 className="row-title text-white">Links</h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-white/40" />}
        </div>
        {links.length === 0 && !loading ? (
          <div className="p-10 text-center text-white/50 text-[13.5px]">No links yet. Create your first tracked link above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead className="bg-white/[0.03] text-white/55">
                <tr>
                  <th className="text-left px-5 py-2 font-semibold">Short URL</th>
                  <th className="text-left px-3 py-2 font-semibold">Destination</th>
                  <th className="text-left px-3 py-2 font-semibold">Source / Campaign</th>
                  <th className="text-right px-3 py-2 font-semibold">Clicks</th>
                  <th className="px-5 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {links.map(l => (
                  <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`link-row-${l.slug}`}>
                    <td className="px-5 py-3 font-mono text-[12.5px]">
                      <a href={shortLinkUrl(l.slug)} target="_blank" rel="noreferrer" className="text-[#22d3ee] hover:underline flex items-center gap-1.5">
                        /l/{l.slug} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-3 py-3 text-white/75 truncate max-w-[280px]">{l.destination_url}</td>
                    <td className="px-3 py-3 text-white/65"><span className="chip mr-1">{l.source || '—'}</span>{l.campaign && <span className="chip">{l.campaign}</span>}</td>
                    <td className="px-3 py-3 text-right font-semibold">{l.clicks || 0}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => doCopy(l.slug)} className="text-white/70 hover:text-white mr-3" aria-label="Copy"><Copy className="w-4 h-4" /></button>
                      <button onClick={() => doDelete(l.id)} className="text-white/60 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ ic: Ic, color, label, value }) {
  return (
    <div className="card-shell p-5">
      <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: color + '22', border: '1px solid ' + color + '55' }}>
        <Ic className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-display text-[30px] mt-3 leading-none">{value}</div>
      <div className="text-[12px] text-white/55 mt-1">{label}</div>
    </div>
  );
}
